import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunks
export const createPaymentIntent = createAsyncThunk(
  'payments/createPaymentIntent',
  async ({ amount, currency = 'usd', orderId, userId }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/payments/create-payment-intent', {
        amount,
        currency,
        orderId,
        userId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create payment intent');
    }
  }
);

export const confirmPayment = createAsyncThunk(
  'payments/confirmPayment',
  async ({ paymentIntentId, orderId }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/payments/confirm-payment', {
        paymentIntentId,
        orderId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to confirm payment');
    }
  }
);

export const fetchPaymentHistory = createAsyncThunk(
  'payments/fetchPaymentHistory',
  async ({ userId, limit = 10, offset = 0 }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.get(`/api/payments/user/${userId}`, {
        params: { limit, offset },
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch payment history');
    }
  }
);

export const requestRefund = createAsyncThunk(
  'payments/requestRefund',
  async ({ paymentIntentId, amount, reason, orderId }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.post('/api/payments/refund',
        { paymentIntentId, amount, reason, orderId },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to process refund');
    }
  }
);

const initialState = {
  clientSecret: null,
  paymentIntentId: null,
  paymentStatus: null,
  paymentHistory: [],
  loading: false,
  processing: false,
  error: null,
};

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    clearPaymentState: (state) => {
      state.clientSecret = null;
      state.paymentIntentId = null;
      state.paymentStatus = null;
      state.error = null;
    },
    setPaymentStatus: (state, action) => {
      state.paymentStatus = action.payload;
    },
    clearPaymentError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create payment intent
      .addCase(createPaymentIntent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPaymentIntent.fulfilled, (state, action) => {
        state.loading = false;
        state.clientSecret = action.payload.clientSecret;
        state.paymentIntentId = action.payload.paymentIntentId;
      })
      .addCase(createPaymentIntent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Confirm payment
      .addCase(confirmPayment.pending, (state) => {
        state.processing = true;
      })
      .addCase(confirmPayment.fulfilled, (state, action) => {
        state.processing = false;
        state.paymentStatus = action.payload.status;
      })
      .addCase(confirmPayment.rejected, (state, action) => {
        state.processing = false;
        state.error = action.payload;
      })
      // Fetch payment history
      .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
        state.paymentHistory = action.payload;
      })
      // Request refund
      .addCase(requestRefund.pending, (state) => {
        state.processing = true;
      })
      .addCase(requestRefund.fulfilled, (state, action) => {
        state.processing = false;
      })
      .addCase(requestRefund.rejected, (state, action) => {
        state.processing = false;
        state.error = action.payload;
      });
  },
});

export const { clearPaymentState, setPaymentStatus, clearPaymentError } = paymentsSlice.actions;
export default paymentsSlice.reducer;
